import styles from "../add.module.css";
import { useState, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { TextField, Checkbox, FormControlLabel } from "@mui/material";
import { Eye, EyeOff } from "react-feather";
import axios from "axios";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { StorageSetItem, STORAGE_KEYS } from "../../../../admin/components/StorageComp";
import { chargePayload } from "../../../../utils/bookingCharges";

/* Final step of the guest booking flow: collects the account/login details
   needed to create the customer record, then submits the booking. Name,
   email, phone and address are pre-filled from the pickup (Step2) details
   so the person doesn't have to retype them — they can still edit them here
   if their account details should differ from the pickup contact. */
const Step5 = ({ bookingData, chargeState, backToTab, setLoading, setSnackbarMessage, setSnackbarType, setSnackbarOpen, websiteData }) => {
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfPassword, setShowConfPassword] = useState(false);

    const [discountVal, setDiscountVal] = useState(0);
    const [discountAmount, setDiscountAmount] = useState(0);
    const [referralDiscount, setReferralDiscount] = useState(0);
    const [subTotalNew, setSubTotalNew] = useState(0);
    const [productRDiscount, setProductRDiscount] = useState(0);
    const [emailStatus, setEmailStatus] = useState(null);
    const emailCheckTimer = useRef(null);

    const { register, handleSubmit, control, formState: { errors }, setValue, watch } = useForm({
        mode: "onChange",
        defaultValues: {
            name: "",
            email: "",
            phone: "",
            password: "",
            address: "",
        },
    });

    // Auto-populate from the pickup (sender) form saved in Step2.
    useEffect(() => {
        setValue("name", bookingData?.pickUpData?.name);
        setValue("email", bookingData?.pickUpData?.email);
        setValue("phone", bookingData?.pickUpData?.phone);
        setValue("address", bookingData?.pickUpData?.address);
    }, [bookingData, setValue]);

    // Same discount-splitting calculation the old confirm step used, needed
    // here to build each line item's final discount/sub_amount for submission.
    useEffect(() => {
        setDiscountVal(bookingData?.discountVal);
        setDiscountAmount(bookingData?.discountAmount);
        setReferralDiscount(bookingData?.referralDiscount);

        let subTotalNewTemp = (bookingData?.products || []).reduce((sum, item) => {
            if (parseFloat(bookingData?.discountVal) > 0) {
                let newDisc = ((parseFloat(item.total) * parseFloat(bookingData?.discountVal)) / 100);
                newDisc = Math.round(newDisc * 100) / 100;

                return sum + parseFloat(item.total) - newDisc;
            }
            return sum + parseFloat(item.total);
        }, 0);

        setSubTotalNew(subTotalNewTemp);
    }, [bookingData]);

    useEffect(() => {
        if (bookingData?.referralDiscount) {
            if (parseFloat(bookingData?.referralDiscount) > 0 && subTotalNew > 0) {
                let refDiscP = parseFloat(bookingData?.referralDiscount) / parseFloat(subTotalNew);
                refDiscP = Math.round(refDiscP * 10000) / 10000;

                setProductRDiscount(refDiscP);
            }
        }
    }, [bookingData?.referralDiscount, subTotalNew]);

    const onSubmit = (accountData) => {
        if (chargeState?.loading || chargeState?.error) {
            setSnackbarOpen(true);
            setSnackbarMessage(chargeState?.error || "Collection and delivery charges are still being calculated.");
            setSnackbarType("error");
            return;
        }
        const chargeData = chargeState?.data;

        setLoading(true);

        let subTotal = 0;
        let noOfItems = bookingData.products.length;

        let products = bookingData.products.map((item) => {
            let newTotal = parseFloat(item.total);
            subTotal += parseFloat(item.total);
            let newDisc = 0;

            if (discountVal > 0) {
                let newDisc1 = ((newTotal * discountVal) / 100);
                newDisc1 = Math.round(newDisc1 * 100) / 100;

                newDisc += newDisc1;
                newTotal = newTotal - newDisc1;
            }
            if (productRDiscount > 0) {
                let newDisc2 = (newTotal * productRDiscount);
                newDisc2 = Math.round(newDisc2 * 100) / 100;

                newDisc += newDisc2;
                newTotal = newTotal - newDisc2;
            }

            let pObj = {
                product_id: item?.item_id,
                product_title: item?.item_name,
                price: item.total,
                quantity: 1,
                subtotal: item.total,
                discount: newDisc,
                sub_amount: newTotal,
                is_fragile: 0,
            };

            if (item?.category_id === -1) {
                pObj = { ...pObj, length: item?.length, width: item?.width, height: item?.height, is_dimension: 1 };
            }

            if (bookingData?.freight_type === 'Air Freight') {
                pObj = { ...pObj, weight: item?.weight }
            }


            return pObj;
        });

        let alt_phones_p = bookingData?.pickUpData?.alt_phones?.map(i => i?.value);
        let alt_phones_r = bookingData?.recipientData?.alt_phones?.map(i => i?.value);

        // NOTE: adjust this endpoint to whatever your backend uses to create a
        // guest account and its first booking together in one call.
        let postData = {
            name: accountData?.name,
            email: accountData?.email,
            phone: accountData?.phone,
            password: accountData?.password,
            address: accountData?.address,
            description: bookingData?.notes,
            no_of_items: noOfItems,
            insurance: bookingData?.insurance,
            company_shipping: bookingData?.company_shipping,
            company_name: bookingData?.company_name,
            registered_company: bookingData?.registered_company,
            pickup_date: moment(bookingData?.pickup_date).format("YYYY-MM-DD"),
            // Discounts only apply to the items; collection/delivery charges are added on top
            total: subTotal - (discountAmount || 0) - (referralDiscount || 0) + (chargeData?.total || 0),
            amount: subTotal,
            post_code: bookingData?.pickUpData?.post_code,
            products: products,
            pickUpData: { ...bookingData?.pickUpData, alt_phones: alt_phones_p },
            recipientData: { ...bookingData?.recipientData, alt_phones: alt_phones_r },
            is_delivery: 0,
            discount_val: discountVal,
            discount_amount: discountAmount,
            referral_discount: referralDiscount,
            utoken: websiteData ? websiteData?.utoken : '',
            freight_type: bookingData?.freight_type || 'Sea Freight',
            ...chargePayload(chargeData),
        };

        axios.post(process.env.REACT_APP_API_URL + "v1/guest-bookings", postData).then((response) => {
            setLoading(false);
            if (response.data.status === "SUCCESS") {
                setSnackbarOpen(true);
                setSnackbarMessage(response.data.message);
                setSnackbarType("success");

                if (response.data.auth) {
                    StorageSetItem(STORAGE_KEYS.AUTH, response.data.auth, 24 * 60 * 60 * 1000);
                }

                navigate("/");
            } else {
                setSnackbarOpen(true);
                setSnackbarMessage(response.data.message || "Something went wrong. Please try again.");
                setSnackbarType("error");
            }
        }).catch((error) => {
            setLoading(false);
            setSnackbarOpen(true);
            setSnackbarMessage(
                error?.response?.data?.message || "Something went wrong. Please try again."
            );
            setSnackbarType("error");
        });
    }

    const checkEmailAvailability = (email) => {
        if (emailCheckTimer.current) {
            clearTimeout(emailCheckTimer.current);
        }

        if (!email) {
            setEmailStatus(null);
            return;
        }

        setEmailStatus("checking");

        emailCheckTimer.current = setTimeout(() => {
            axios
                .post(process.env.REACT_APP_API_URL + "v1/check-email", {
                    email,
                    utoken: websiteData ? websiteData?.utoken : '',
                })
                .then((response) => {
                    if (response.data.status === "SUCCESS") {
                        setEmailStatus(
                            response.data.data.is_available ? "available" : "taken"
                        );
                    } else {
                        setEmailStatus("error");
                    }
                })
                .catch(() => {
                    setEmailStatus("error");
                });
        }, 500);
    }

    return (<>
        <div className={`${styles.CustomWidth}`}>
            <p className={styles.SectionLabel}>Account details</p>
            <p className={styles.HelperNote}>We&apos;ve filled these in from your pickup details — feel free to change anything below.</p>

            <form onSubmit={handleSubmit(onSubmit)}>
                <div className={styles.FormFullGroup}>
                    <Controller
                        control={control}
                        {...register("name", { required: "This field is Required" })}
                        render={({ field }) => (<TextField {...field} label="Name*" variant="outlined" fullWidth />)}
                    />
                    {errors.name && (<span className={styles.ErrorM}>{errors?.name?.message}</span>)}
                </div>

                <div className={styles.FormFullGroup}>
                    <Controller
                        control={control}
                        {...register("email", {
                            required: "This field is Required",
                            pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: "Invalid email address",
                            },
                            validate: async (value) => {
                                if (!value) return true;

                                try {
                                    const response = await axios.post(
                                        process.env.REACT_APP_API_URL + "v1/check-email",
                                        { email: value, utoken: websiteData ? websiteData?.utoken : '' }
                                    );

                                    if (
                                        response.data.status === "SUCCESS" &&
                                        response.data.data.is_available
                                    ) {
                                        return true;
                                    }

                                    return "This email is already used";
                                } catch (error) {
                                    return "Unable to verify email";
                                }
                            },
                        })}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                classes={{ root: styles.inputRoot }}
                                fullWidth
                                label="Email*"
                                variant="outlined"
                                onChange={(e) => {
                                    field.onChange(e);
                                    checkEmailAvailability(e.target.value);
                                }}
                            />
                        )}
                    />
                    {errors.email ? (
                        <span className={styles.ErrorM}>{errors.email.message}</span>
                    ) : emailStatus === "checking" ? (
                        <span className={styles.FieldHint}>Checking email...</span>
                    ) : emailStatus === "available" ? (
                        <span className={styles.FieldSuccess}>Email is available</span>
                    ) : emailStatus === "taken" ? (
                        <span className={styles.ErrorM}>This email is already used</span>
                    ) : null}
                </div>

                <div className={styles.FormFullGroup}>
                    <Controller
                        control={control}
                        {...register("phone", { required: "This field is Required" })}
                        render={({ field }) => (<TextField {...field} label="Phone*" variant="outlined" fullWidth />)}
                    />
                    {errors.phone && (<span className={styles.ErrorM}>{errors.phone.message}</span>)}
                </div>

                <div className={styles.FormFullGroup}>
                    <div className={styles.PasswordFieldWrap}>
                        <Controller
                            control={control}
                            {...register("password", {
                                required: "This field is Required",
                                minLength: { value: 6, message: "Password must be at least 6 characters" }
                            })}
                            render={({ field }) => (<TextField {...field} type={showPassword ? "text" : "password"} label="Password*" variant="outlined" fullWidth />)}
                        />
                        <button type="button" className={styles.PasswordToggleBU} onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? "Hide password" : "Show password"}>
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    </div>
                    {errors.password && (<span className={styles.ErrorM}>{errors?.password?.message}</span>)}
                </div>

                <div className={styles.FormFullGroup}>
                    <div className={styles.PasswordFieldWrap}>
                        <Controller
                            control={control}
                            {...register("conf_password", {
                                required: "This field is Required",
                                validate: (val) => {
                                    if (watch("password") !== val) {
                                        return "Your passwords do no match";
                                    }
                                },
                            })}
                            render={({ field }) => (<TextField {...field} type={showConfPassword ? "text" : "password"} label="Confirm Password*" variant="outlined" fullWidth />)}
                        />
                        <button type="button" className={styles.PasswordToggleBU} onClick={() => setShowConfPassword((v) => !v)} aria-label={showConfPassword ? "Hide password" : "Show password"}>
                            {showConfPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    </div>
                    {errors.conf_password && (<span className={styles.ErrorM}>{errors?.conf_password?.message}</span>)}
                </div>

                <div className={styles.FormFullGroup}>
                    <Controller
                        control={control}
                        {...register("address", { required: "This field is Required" })}
                        render={({ field }) => (<TextField {...field} label="Address*" variant="outlined" fullWidth multiline rows={2} />)}
                    />
                    {errors?.address && (<p className={styles.ErrorM}>{errors?.address?.message}</p>)}
                </div>

                <FormControlLabel
                    control={<Checkbox {...register("is_accept", { required: true })} />}
                    label="I confirm the details above are correct and agree to create an account *"
                    className={styles.CheckBox}
                />
                {errors?.is_accept && (<p className={styles.ErrorM}>Please accept terms & condition</p>)}

                <div className={`${styles.StepBUSec}`}>
                    <button type="button" onClick={backToTab} className={`${styles.Previous}`}>Back</button>
                    <button type="submit" className={`${styles.Next} ${styles.Confirm}`}>Confirm Booking</button>
                </div>
            </form>
        </div>
    </>);
}

export default Step5;